import React, { useEffect, useState } from "react";
import PayoutsAPI from "../../apis/PayoutsAPI.jsx";

/* PayoutHistory — terminal-styled paginated history of outbound
   payouts, filterable by rail and status. Each row exposes a Cancel
   button when the transaction is still cancellable (status
   SUBMITTED / PENDING / PROCESSING / INITIAL). Mounted as the right
   column of the Pay tab "global" mode.

   Per PAYOUTS_V2_FE_INTEGRATION.md §Listing history, V1 and V2
   ledgers are independent. The history view queries BOTH and merges
   client-side until the entire history is migrated. Each row carries
   _source so cancel goes to the right endpoint. */

const STATUS_COLOR = {
  COMPLETED:  "var(--term-green)",
  COMPLETE:   "var(--term-green)",
  SUBMITTED:  "var(--term-amber)",
  PENDING:    "var(--term-amber)",
  PROCESSING: "var(--term-amber)",
  INITIAL:    "var(--term-amber)",
  FAILED:     "var(--term-red)",
  CANCELLED:  "var(--term-ink-3)",
  CANCELED:   "var(--term-ink-3)",
  EXPIRED:    "var(--term-ink-3)",
};

/* Normalize V1 + V2 row shapes into a single view-model. Keeps the
   table rendering uniform without forcing the backend to align field
   names across ledgers. */
function normalizeRow(r, source) {
  if (source === "v2") {
    return {
      _source:      "v2",
      _id:          r.payout_id,
      amount:       r.amount,
      currency:     r.currency,
      status:       r.status,
      counterparty: r.recipient?.account_holder_name || r.recipient?.name || "—",
      created_on:   r.created_on,
      payment_type: r.rail_id || r.method,
      rail_id:      r.rail_id,
    };
  }
  return {
    _source:      "v1",
    _id:          r.transaction_id,
    amount:       r.amount,
    currency:     r.currency,
    status:       r.status,
    counterparty: r.counterparty || "—",
    created_on:   r.created_on,
    payment_type: r.payment_type || r.rail_id,
    rail_id:      r.rail_id,
  };
}

function fmtTime(iso) {
  if (!iso) return "—";
  const s = String(iso);
  return s.length >= 16 ? s.slice(5, 16).replace("T", " ") : s;
}

export default function PayoutHistory({ token, refreshKey = 0 }) {
  const [rows, setRows]             = useState([]);
  const [railFilter, setRailFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState(null);
  const [cancelling, setCancelling] = useState(null);

  const load = () => {
    if (!token) return;
    setLoading(true);
    setError(null);
    // Hit both ledgers in parallel; either one failing is non-fatal —
    // we still show whatever rows came back from the other.
    Promise.all([
      PayoutsAPI.listPayoutsV2(token, {
        railId: railFilter || undefined,
        status: statusFilter || undefined,
        limit: 50,
      }).catch((e) => ({ _err: e })),
      PayoutsAPI.listTransactions(token, {
        railId: railFilter || undefined,
        status: statusFilter || undefined,
        limit: 50,
      }).catch((e) => ({ _err: e })),
    ])
      .then(([v2, v1]) => {
        const v2Rows = (v2?._err ? [] : (v2?.data?.payouts || [])).map((r) => normalizeRow(r, "v2"));
        const v1Rows = (v1?._err ? [] : (v1?.data?.transactions || [])).map((r) => normalizeRow(r, "v1"));
        const merged = [...v2Rows, ...v1Rows].sort((a, b) => {
          const ta = a.created_on ? Date.parse(a.created_on) : 0;
          const tb = b.created_on ? Date.parse(b.created_on) : 0;
          return tb - ta;
        });
        setRows(merged);
        // Surface an error only when BOTH ledgers fail — partial failure
        // is normal during V2 rollout and shouldn't block the user.
        if (v2?._err && v1?._err) {
          const body = v1._err?.response?.data || v2._err?.response?.data || {};
          setError(body.message || body.payload || (v1._err?.message || v2._err?.message));
        }
        setLoading(false);
      });
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [token, railFilter, statusFilter, refreshKey]);

  const handleCancel = (row) => {
    setCancelling(row._id);
    const call = row._source === "v2"
      ? PayoutsAPI.cancelPayoutV2(token, row._id)
      : PayoutsAPI.cancelTransaction(token, row._id);
    call
      .then((resp) => {
        // V2 returns { success: false, error: "CANNOT_CANCEL" } as a
        // 200 envelope per spec; catch that here rather than only on
        // a thrown promise.
        const body = resp?.data || {};
        if (body.success === false) {
          const reason = body.message || body.error || "refused";
          setError(`Cancel refused: ${reason}`);
        }
        setCancelling(null);
        load();
      })
      .catch((e) => {
        const body = e?.response?.data || {};
        const reason = body.message || body.reason || body.payload || e.message;
        setError(`Cancel refused: ${reason}`);
        setCancelling(null);
      });
  };

  return (
    <div className="term-pane" style={{ display:"flex", flexDirection:"column", minHeight:0 }}>
      <div className="term-pane-head">
        Payout history
        <span className="meta">
          {loading ? "loading…" : <><b>{rows.length}</b> shown</>}
        </span>
      </div>

      <div style={{ padding:"10px 14px", borderBottom:"1px solid var(--term-line)", display:"flex", gap:8, background:"var(--term-bg-pane)" }}>
        <select
          value={railFilter}
          onChange={(e) => setRailFilter(e.target.value)}
          style={{
            flex:1, padding:"6px 8px", background:"var(--term-bg-pane-2)",
            border:"1px solid var(--term-line)", color:"var(--term-ink-1)",
            fontFamily:"inherit", fontSize:11, outline:"none",
          }}
        >
          <option value="">All rails</option>
          <option value="pse">PSE</option>
          <option value="breb">Bre-B</option>
          <option value="efecty">Efecty</option>
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{
            flex:1, padding:"6px 8px", background:"var(--term-bg-pane-2)",
            border:"1px solid var(--term-line)", color:"var(--term-ink-1)",
            fontFamily:"inherit", fontSize:11, outline:"none",
          }}
        >
          <option value="">All statuses</option>
          <option value="completed">Completed</option>
          <option value="pending">Pending</option>
          <option value="processing">Processing</option>
          <option value="initial">Initial</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      <div style={{ flex:1, overflow:"auto" }}>
        {error && (
          <div className="mono" style={{ padding:"10px 14px", fontSize:10.5, color:"var(--term-red)", borderBottom:"1px solid var(--term-line-dim)" }}>
            {String(error)}
          </div>
        )}
        {!loading && rows.length === 0 && !error && (
          <div className="mono" style={{ padding:"24px 14px", fontSize:11.5, color:"var(--term-ink-3)", textAlign:"center" }}>
            no payouts yet
          </div>
        )}
        {rows.map((r) => {
          const cancellable = /pending|processing|initial|submitted/i.test(r.status || "");
          const stColor = STATUS_COLOR[String(r.status || "").toUpperCase()] || "var(--term-ink-2)";
          return (
            <div key={`${r._source}:${r._id}`} style={{
              padding:"10px 14px", borderBottom:"1px solid var(--term-line-dim)",
              display:"grid", gridTemplateColumns:"1fr auto", gap:6,
            }}>
              <div style={{ minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"baseline", gap:8 }}>
                  <span className="mono" style={{ fontSize:11.5, color:"var(--term-ink-1)", fontWeight:500 }}>
                    {Number(r.amount).toLocaleString("en-US")} {r.currency}
                  </span>
                  <span className="mono" style={{ fontSize:9, letterSpacing:"0.12em", textTransform:"uppercase", fontWeight:600, color: stColor }}>
                    {r.status || "—"}
                  </span>
                </div>
                <div className="mono" style={{ fontSize:9.5, color:"var(--term-ink-3)", marginTop:2, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>
                  {r.counterparty || "—"}
                </div>
                <div className="mono" style={{ fontSize:9, color:"var(--term-ink-3)", marginTop:2, letterSpacing:"0.04em" }}>
                  {fmtTime(r.created_on)} · {r.payment_type || "—"}
                </div>
              </div>
              {cancellable && (
                <button
                  onClick={() => handleCancel(r)}
                  disabled={cancelling === r._id}
                  className="term-util-btn"
                  style={{ alignSelf:"center", color:"var(--term-amber)", borderColor:"var(--term-amber)" }}
                >
                  {cancelling === r._id ? "…" : "Cancel"}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
